import { Alert, Button, Loading, Modal, Typography } from "@/components/atoms";
import { PlanChangePreview, SubscriptionPlan } from "@/redux/apis/subscription/types";
import { formatCurrency } from "@/utils/formatCurrency";

interface ConfirmUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  targetPlan?: SubscriptionPlan;
  /** Plan-change preview carrying the exact immediate charge (cents). */
  preview?: PlanChangePreview;
  /** True while the preview (charge amount) is loading. */
  isPreviewLoading?: boolean;
  /** True while the upgrade is being applied (spinner over the modal). */
  isLoading?: boolean;
}

/**
 * Post-trial upgrade confirmation. Because an upgrade charges the card
 * immediately, we confirm the exact amount (prorated delta + any setup fee)
 * before the charge, then show a spinner while the upgrade runs.
 */
export const ConfirmUpgradeModal = ({
  open,
  onClose,
  onConfirm,
  targetPlan,
  preview,
  isPreviewLoading = false,
  isLoading = false,
}: ConfirmUpgradeModalProps) => {
  const planName = targetPlan?.subscriptionName || preview?.newPlan.name || "New Plan";
  const iconSvg = targetPlan?.iconSvg;
  const currency = preview?.currency || "AUD";

  const proratedAmount = preview?.proratedAmount ?? 0;
  const setupFeeAmount = preview?.setupFeeAmount ?? 0;
  const total = preview?.immediateChargeAmount ?? proratedAmount + setupFeeAmount;

  const money = (cents: number) => formatCurrency(cents, 2, currency, { appendCurrency: true });

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[458px] lg:w-[458px]">
      <div className="relative flex flex-col gap-4">
        {isLoading && <Loading />}

        {/* Title and Description */}
        <div className="flex flex-col gap-2 text-neutral-dark-gray">
          <Typography variant="title">Confirm Upgrade</Typography>
          <Typography variant="paragraph">
            You&apos;re upgrading to <span className="font-semibold">{planName} Plan</span>. Your card will be charged now:
          </Typography>
        </div>

        {/* Plan Header */}
        <div className="flex flex-row items-center gap-3">
          {iconSvg && (
            <div className="flex h-10 w-10 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: iconSvg }} />
          )}
          <Typography variant="title">{planName}</Typography>
        </div>

        {/* Charge breakdown */}
        <div className="flex flex-col gap-3 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4">
          {isPreviewLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loading />
            </div>
          ) : (
            <>
              <div className="flex justify-between">
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Prorated charge (rest of billing period)
                </Typography>
                <Typography variant="paragraph">{money(proratedAmount)}</Typography>
              </div>
              {setupFeeAmount > 0 && (
                <div className="flex justify-between">
                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    One-time setup fee
                  </Typography>
                  <Typography variant="paragraph">{money(setupFeeAmount)}</Typography>
                </div>
              )}
              <div className="flex justify-between border-t border-neutral-mid-gray pt-3">
                <Typography variant="action">Total charged now</Typography>
                <Typography variant="action">{money(total)}</Typography>
              </div>
            </>
          )}
        </div>

        {/* Info note */}
        <Alert
          type="info"
          message={
            <Typography variant="paragraph">
              Your new plan starts immediately. This charge covers the remaining days in your current billing period; your regular
              subscription fee applies from the next cycle.
            </Typography>
          }
          noIcon
        />

        {/* Action Buttons */}
        <div className="flex flex-row items-center justify-end gap-3">
          <Button type="button" size="lg" variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="button" size="lg" variant="primary" onClick={handleConfirm} disabled={isLoading || isPreviewLoading}>
            Confirm Upgrade
          </Button>
        </div>
      </div>
    </Modal>
  );
};
